"use client";

import { useCallback, useMemo } from "react";
import type { Collab } from "@cabal/shared/domain/session/collab/types.js";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import type { VersionSummary } from "@cabal/shared/domain/session/version/types.js";
import { useSessionDataContext, type SessionDataContextValue } from "./context";
import {
  selectEntityShortId,
  selectHydratedVersion,
  selectLoadedProductCollabs,
  selectProductById,
  selectResolvedShortId,
  selectVersionHydrationTarget,
  selectVersionProductIdMap,
  type ResolvedShortId,
  type SessionEntityLookupSources,
} from "./entity-lookup-selectors";

export type { ResolvedShortId } from "./entity-lookup-selectors";

interface SessionEntityLookup {
  /** Resolve a canonical short-id (`p3`, `v7`, `m2`, `c5`, `u1`, …) — or
   *  the version `formatted` alias `p#/#` — against the unified provider
   *  index. Returns `null` when the short-id is malformed or unknown. */
  resolveShortId: (shortId: string | null | undefined) => ResolvedShortId | null;
  /** The `ProductSummary` a product UUID names — for callers that hold a
   *  parent id off another entity (a version's `productId`) rather than a
   *  short id. Returns `null` when the product is not in the session. */
  resolveProductById: (
    productId: string | null | undefined,
  ) => ProductSummary | null;
  /** Reverse direction: given an entity UUID, return its `{ canonical,
   *  formatted }` short-id pair. Used by URL writers and `data-*` attribute
   *  emitters that have a UUID handle and need to render a short-id. */
  formatEntityShortId: (
    entityId: string | null | undefined,
  ) => { canonical: string; formatted: string } | null;
  ensureVersion: (
    versionId: string | null | undefined,
  ) => Promise<VersionSummary | null>;
  ensureProductCollabs: (
    productId: string | null | undefined,
  ) => Promise<Collab[] | null>;
}

export function useSessionEntityLookup(): SessionEntityLookup {
  const context = useSessionDataContext();
  const versionToProductMap = useVersionToProductMap({
    products: context.products,
  });
  const lookupSources = useSessionEntityLookupSources({ context });
  const resolveShortId = useResolvedShortIdCallback({
    fromShortId: context.fromShortId,
    lookupSources,
  });
  const resolveProductById = useResolveProductByIdCallback({
    products: context.products,
  });
  const formatEntityShortId = useFormatEntityShortIdCallback({
    toShortId: context.toShortId,
  });
  const ensureVersion = useEnsureVersionCallback({
    actions: context.actions,
    versions: context.versions,
    versionProductIds: versionToProductMap,
  });
  const ensureProductCollabs = useEnsureProductCollabsCallback({
    actions: context.actions,
    collabsByProduct: context.collabsByProduct,
    collabs: context.collabs,
    chatMessages: context.chatMessages,
  });

  return {
    resolveShortId,
    resolveProductById,
    formatEntityShortId,
    ensureVersion,
    ensureProductCollabs,
  };
}

function useVersionToProductMap(input: {
  products: Record<string, ProductSummary>;
}): Record<string, string> {
  const { products } = input;

  return useMemo(
    () => selectVersionProductIdMap({ products }),
    [products],
  );
}

function useSessionEntityLookupSources(input: {
  context: SessionDataContextValue;
}): SessionEntityLookupSources {
  const { context } = input;
  const products = context.products;
  const versions = context.versions;
  const attachedFiles = context.attachedFiles;
  const members = context.members;
  const collabs = context.collabs;
  const chatMessages = context.chatMessages;
  const versionPreviews = context.versionPreviews;
  const collabPreviews = context.collabPreviews;
  const reflectionPreviews = context.reflectionPreviews;

  return useMemo(
    () => ({
      products,
      versions,
      attachedFiles,
      members,
      collabs,
      chatMessages,
      versionPreviews,
      collabPreviews,
      reflectionPreviews,
    }),
    [
      products,
      versions,
      attachedFiles,
      members,
      collabs,
      chatMessages,
      versionPreviews,
      collabPreviews,
      reflectionPreviews,
    ],
  );
}

function useResolvedShortIdCallback(input: {
  fromShortId: SessionDataContextValue["fromShortId"];
  lookupSources: SessionEntityLookupSources;
}): SessionEntityLookup["resolveShortId"] {
  const { fromShortId, lookupSources } = input;

  return useCallback(
    (shortId: string | null | undefined): ResolvedShortId | null =>
      selectResolvedShortId({
        shortId,
        fromShortId,
        sources: lookupSources,
      }),
    [fromShortId, lookupSources],
  );
}

function useResolveProductByIdCallback(input: {
  products: Record<string, ProductSummary>;
}): SessionEntityLookup["resolveProductById"] {
  const { products } = input;

  return useCallback(
    (productId: string | null | undefined): ProductSummary | null =>
      selectProductById({ productId, products }),
    [products],
  );
}

function useFormatEntityShortIdCallback(input: {
  toShortId: SessionDataContextValue["toShortId"];
}): SessionEntityLookup["formatEntityShortId"] {
  const { toShortId } = input;

  return useCallback(
    (entityId: string | null | undefined) =>
      selectEntityShortId({ entityId, toShortId }),
    [toShortId],
  );
}

function useEnsureVersionCallback(input: {
  actions: SessionDataContextValue["actions"];
  versions: Record<string, VersionSummary>;
  versionProductIds: Record<string, string>;
}): SessionEntityLookup["ensureVersion"] {
  const { actions, versions, versionProductIds } = input;

  return useCallback(
    async (versionId: string | null | undefined): Promise<VersionSummary | null> => {
      const versionHydrationTarget = selectVersionHydrationTarget({
        versionId,
        versions,
        versionProductIds,
      });
      if (!versionHydrationTarget.normalizedVersionId) return null;
      if (versionHydrationTarget.cachedVersion) {
        return versionHydrationTarget.cachedVersion;
      }

      const productId = versionHydrationTarget.productId;
      if (!productId) return null;

      const hydratedVersions = await actions.ensureProductVersions(productId);
      return selectHydratedVersion({
        normalizedVersionId: versionHydrationTarget.normalizedVersionId,
        hydratedVersions,
        versions,
      });
    },
    [actions, versions, versionProductIds],
  );
}

function useEnsureProductCollabsCallback(input: {
  actions: SessionDataContextValue["actions"];
  collabsByProduct: SessionDataContextValue["collabsByProduct"];
  collabs: SessionDataContextValue["collabs"];
  chatMessages: SessionDataContextValue["chatMessages"];
}): SessionEntityLookup["ensureProductCollabs"] {
  const { actions, collabsByProduct, collabs, chatMessages } = input;

  return useCallback(
    async (productId: string | null | undefined): Promise<Collab[] | null> => {
      const loadedProductCollabs = selectLoadedProductCollabs({
        productId,
        collabsByProduct,
        collabs,
        chatMessages,
      });
      if (!loadedProductCollabs.normalizedProductId) return null;
      if (loadedProductCollabs.hasLoadedProductCollabs) {
        return loadedProductCollabs.collabs;
      }

      return actions.ensureProductCollabs(
        loadedProductCollabs.normalizedProductId,
      );
    },
    [actions, collabsByProduct, collabs, chatMessages],
  );
}
